import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import { centsToDollarsNumber, getLocalizedPricing } from '../lib/util/money';
import * as types from '../types';
import { isModuleSubscriptionProduct } from '../types';

/**
 * Returns the localized price for the 1-year free trial subscription.
 *
 * @param collarProductId The product ID of the collar (e.g., miniCollarId or series3PlusCollarId), or null
 * @returns The formatted price string (e.g., "$189" for USD or "$249" for CAD), or null if collarProductId is null or no subscription found
 */
export default function useFreeTrialSubscriptionPrice(collarProductId: string | null): string | null {
  const { currency, currencyFormatter } = useLocale();
  const products = useSelector((state: types.AppState) => state.config.products);

  const freeTrialSubscription = useMemo(() => {
    if (!collarProductId) {
      return null;
    }

    const subscriptionProducts = products
      .filter(isModuleSubscriptionProduct)
      .filter((product) => product.canBePurchasedForProductIds.includes(collarProductId));

    const freeTrialProduct = subscriptionProducts.find((product) => product.freeTrial === true);

    return freeTrialProduct ?? null;
  }, [products, collarProductId]);

  if (!freeTrialSubscription) {
    return null;
  }

  const priceInCents = getLocalizedPricing(freeTrialSubscription.pricingInCents, currency);
  return currencyFormatter.format(centsToDollarsNumber(priceInCents));
}
